import React, { useState } from 'react';
import type { Message } from '../../shared/types/src';
import { useAuth } from '../../context/shared/AuthContext';
import { useTheme } from '../../context/shared/ThemeContext';
import apiService from '../../services/shared/apiService';
import SymbolicOverrideModal from './SymbolicOverrideModal';
import { BookmarkIcon } from '@heroicons/react/24/outline';

interface ChatMessageProps {
  message: Message;
  userPrompt?: Message;
  onSave?: (aiMessage: Message, userPrompt?: Message) => void;
  isLoading?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, userPrompt, onSave, isLoading }) => {
  const { user } = useAuth();
  const { themeClasses } = useTheme();
  const isAI = message.sender === 'ai';
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [isSavingAnchor, setIsSavingAnchor] = useState(false);
  
  const getBubbleColor = () => {
    if (!isAI) return 'bg-purple-600';
    switch (message.llm_source?.toLowerCase()) {
      case 'claude':
        return 'bg-blue-500';
      case 'openai':
      case 'gpt':
        return 'bg-blue-600';
      default:
        return 'bg-blue-500';
    }
  };

  const getAIName = () => {
    if (!isAI) return '';
    switch (message.llm_source?.toLowerCase()) {
      case 'claude':
        return 'Claude said:';
      case 'openai':
      case 'gpt':
        return 'GPT said:';
      case 'system':
        return 'Mentra said:';
      default:
        return 'AI Assistant said:';
    }
  };

  const handleOverrideComplete = () => {
    // Refresh the page or trigger a refetch of messages
    window.location.reload();
  };

  const handleSaveAsAnchor = async () => {
    if (!user || !message.meta?.symbolic_tags) return;
    
    try {
      setIsSavingAnchor(true);
      
      // Create a title from the first few words of the message
      const title = message.text.substring(0, 50) + (message.text.length > 50 ? '...' : '');
      
      await apiService.createMemoryAnchor({
        title,
        date: message.timestamp || new Date().toISOString(),
        tagsPresent: message.meta.symbolic_tags,
        reflectionNote: `AI response: ${message.text.substring(0, 200)}${message.text.length > 200 ? '...' : ''}`,
        sourceType: 'chat_message',
        sourceId: message.id,
        radarScores: message.meta.radar_scores || {}
      });
      
      // Show success feedback
      alert('Memory anchor created successfully!');
    } catch (error) {
      console.error('Error saving anchor:', error);
      alert('Failed to create memory anchor. Please try again.');
    } finally {
      setIsSavingAnchor(false);
    }
  };

  return (
    <>
      <div className={`flex ${isAI ? 'justify-start' : 'justify-end'} from group`}>
        <div className={`relative max-w-[80%] ${isAI ? 'mr-auto' : 'ml-auto'} group`}>
          {/* Attribution (e.g., "Claude said:") is currently rendered outside the chat bubble for clarity.
          // TODO: Move this inside the bubble if a more integrated look is desired. */}
          {isAI && (
            <div className="text-sm text-gray-600 mb-1">
              {getAIName()}
            </div>
          )}
          <div
            className={`p-4 rounded-lg ${getBubbleColor()} text-white relative ${
              isLoading ? 'opacity-70' : ''
            }`}
          >
            <div className="whitespace-pre-wrap">
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white/80 rounded-full animate-[bounce_1s_infinite] animate-pulse" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-white/80 rounded-full animate-[bounce_1s_infinite] animate-pulse" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-white/80 rounded-full animate-[bounce_1s_infinite] animate-pulse" style={{ animationDelay: '300ms' }} />
                  <div className="w-2 h-2 bg-white/80 rounded-full animate-[bounce_1s_infinite] animate-pulse" style={{ animationDelay: '450ms' }} />
                </div>
              ) : (
                message.text
              )}
            </div>
            
            {/* Symbolic Tags Display */}
            {message.meta?.symbolic_tags && message.meta.symbolic_tags.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/20">
                <div className="text-xs text-white/80 mb-2 flex items-center justify-between">
                  <span>Patterns Detected:</span>
                  <button
                    onClick={() => setIsOverrideModalOpen(true)}
                    className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded transition-colors"
                    title="Not quite right? Adjust these tags"
                  >
                    Adjust
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {message.meta.symbolic_tags.map((tag: string, index: number) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-white/20 text-white text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            {isAI && !isLoading && (
              <div className="absolute -right-16 top-1/2 transform -translate-y-1/2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
                {onSave && (
                  <button
                    onClick={() => onSave(message, userPrompt)}
                    className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 hover:scale-110 transition-all shadow-md"
                    title="Save message"
                  >
                    💾
                  </button>
                )}
                {message.meta?.symbolic_tags && message.meta.symbolic_tags.length > 0 && (
                  <button
                    onClick={handleSaveAsAnchor}
                    disabled={isSavingAnchor}
                    className={`p-2 ${themeClasses.secondaryText} hover:text-indigo-600 hover:scale-110 transition-all disabled:opacity-50`}
                    title="Save as memory anchor"
                  >
                    {isSavingAnchor ? (
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <BookmarkIcon className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
          
          {message.timestamp && (
            <div className="text-xs text-gray-500  mt-1">
              {new Date(message.timestamp).toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* Symbolic Override Modal */}
      {message.meta?.symbolic_tags && user && (
        <SymbolicOverrideModal
          isOpen={isOverrideModalOpen}
          onClose={() => setIsOverrideModalOpen(false)}
          messageId={message.id || ''}
          originalTags={message.meta.symbolic_tags}
          userId={user.uid}
          onOverrideComplete={handleOverrideComplete}
        />
      )}
    </>
  );
};

export default ChatMessage; 